import * as React from 'react'
import styled, { css } from 'styled-components'

import * as utils from '@owl-nest/utils'
import * as entity from '@boutique/entities'

import * as plume from '@ulule/owl-kit-components/next'

import type { SelectedOptions } from './hooks'
import type { FormattedOption } from './utils'
import { SizeGuide } from './SizeGuide'

type OptionsProps = {
  id?: string
  formattedOptions: {
    sizeOptions: FormattedOption[]
    nonSizeOptions: FormattedOption[]
  }
  product: entity.product.Product
  selectedOptions: SelectedOptions
  onOptionSelect: (optionName: string, optionValue: string) => void
}

export function Options({
  id,
  product,
  formattedOptions: { sizeOptions, nonSizeOptions },
  selectedOptions,
  onOptionSelect,
}: OptionsProps): React.ReactElement<OptionsProps> {
  const [sizeGuideVisible, setSizeGuideAsVisible] = React.useState(false)

  const hasSizeGuide =
    'identifier' in product.brand &&
    (product.brand.sizeGuide !== null || product.sizeGuideImage !== null || product.sizeGuideText !== null)

  return (
    <OptionsWrapper>
      <span id={id} />
      {sizeOptions.length !== 0 && (
        <>
          <SizeOptionGrid>
            {sizeOptions.map((option) => {
              return (
                <React.Fragment key={option.name}>
                  {sizeOptions.length > 1 && <SizeOptionLabel>{option.name}</SizeOptionLabel>}
                  <SizeOptionValues>
                    {option.values.map(({ available, name }) => (
                      <OptionButton
                        selected={name === selectedOptions[option.name]}
                        disabled={!available}
                        key={name}
                        onClick={() => onOptionSelect(option.name, name)}
                      >
                        {name}
                      </OptionButton>
                    ))}
                  </SizeOptionValues>
                </React.Fragment>
              )
            })}
          </SizeOptionGrid>
        </>
      )}

      {hasSizeGuide && (
        <SizeGuideLinkWrapper>
          <SizeGuideLink
            underlined
            onClick={() => {
              setSizeGuideAsVisible(true)
            }}
          >
            <plume.styles.copy.S>Guide des tailles</plume.styles.copy.S>
          </SizeGuideLink>

          <SizeGuideModal
            closable
            lockScroll
            onClose={() => {
              setSizeGuideAsVisible(false)
            }}
            open={sizeGuideVisible}
          >
            <plume.styles.copy.L>Guide des tailles</plume.styles.copy.L>
            <SizeGuide product={product} />
          </SizeGuideModal>
        </SizeGuideLinkWrapper>
      )}

      {nonSizeOptions.length > 0 && (
        <NonSizeOptions>
          {nonSizeOptions.map((option, index) => {
            const optionName = option.name !== 'Title' ? utils.text.capitalize(option.name) : 'Options'

            if (option.values.length === 1) {
              return <React.Fragment key={option.name}></React.Fragment>
            }

            return (
              <Select
                key={option.name}
                height={300}
                native
                onChange={(selectedValues: { label: string; value: string }[]) => {
                  onOptionSelect(option.name, selectedValues[0].value)
                }}
                placeholder={optionName}
                label={(selected, placeholder) => {
                  if (selected.length === 0) {
                    return placeholder
                  }

                  return `${optionName} : ${selected[0].label}`
                }}
                selected={[selectedOptions[option.name]]}
                zIndex={20 - index}
              >
                {option.values
                  .filter(({ name }) => name !== 'Default Title')
                  .map(({ name }) => {
                    return (
                      <plume.Option native={true} key={name} value={name} label={name}>
                        {name}
                      </plume.Option>
                    )
                  })}
              </Select>
            )
          })}
        </NonSizeOptions>
      )}
    </OptionsWrapper>
  )
}

const Select = styled(plume.Select)<{ zIndex: number }>`
  z-index: ${({ zIndex }) => zIndex};
  position: relative;

  ${plume.styles.select.Label} {
    max-width: calc(100% - 30px);
  }
`

const SizeGuideModal = styled(plume.Modal)`
  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.styles.modal.Wrapper} {
      max-width: 75vw;
      max-height: 75vh;
    }
    ${plume.styles.modal.Body} {
      max-height: 75vh;
      display: flex;
      flex-direction: column;
    }
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.styles.modal.Wrapper} {
      max-width: 50vw;
    }
  }

  ${plume.styles.modal.Body} {
    overflow: auto;
  }

  ${plume.styles.copy.L} {
    font-weight: 600;
  }
`

const OptionButton = styled.button<{ selected: boolean }>`
  cursor: pointer;
  height: 50px;
  min-width: 50px;
  margin: 0px 14px 14px 0;
  border-radius: 2px;
  border: 1px solid ${plume.COLORS.GREY_SHADE_7};
  padding: 0 12px;

  &:disabled {
    opacity: 0.5;
    cursor: initial;
  }

  &:focus {
    outline: none;
  }

  ${({ selected }) => {
    if (selected) {
      return css`
        border-color: ${plume.COLORS.PRIMARY_BLUE};
        color: ${plume.COLORS.PRIMARY_BLUE};
      `
    }
  }}

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    height: 46px;
    min-width: 46px;
    margin: 0px 12px 12px 0;
  }
`
const NonSizeOptions = styled.div`
  ${plume.Select} {
    margin-bottom: 16px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.Select} {
      width: 296px;
    }
  }
`
const SizeGuideLink = styled(plume.Link)`
  display: inline;

  ${plume.styles.copy.S} {
    display: inline;
    font-weight: 600;
  }
`

const SizeOptionLabel = styled(plume.styles.copy.M)`
  margin: 0 20px 10px 0;
  font-weight: 600;
`

const SizeOptionValues = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 0;
  }
`

const SizeOptionGrid = styled.div`
  @media screen and ${plume.BREAKPOINTS.TABLET} {
    align-items: baseline;
  }
`

const OptionsWrapper = styled.div`
  margin-bottom: 4px;
`

const SizeGuideLinkWrapper = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
`
